import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { supabase, isInvalidRefreshTokenError } from '../libs/supabaseClient';  // Use shared function
import { User, Session, RealtimeChannel } from '@supabase/supabase-js';
import { sync } from '../libs/syncClient';  // Import sync to trigger after login (aligned with mobile)
import { authService } from '../services/auth';  // Shared auth service
import { permissionsService } from '../services/permissionsService';
import { purgeUnauthorizedLocalData } from '../services/security/localDataSecurityService';
import { marcarHaIngresado } from '../helpers/ctaPublico';
import type { Action, Module, Role, UserCompany, UserPermissions } from '../types/permissions';

// Definimos el tipo para el valor del contexto
export interface AuthContextType {
  user: User | null;
  /**
   * Rol de SITIO (site-wide) leído de profiles.role: 'usuario' | 'admin'.
   * Sirve para cosas globales como moderar el foro, devtools y hoja de ruta.
   * Es DISTINTO del rol de empresa (userRole).
   */
  role: 'usuario' | 'admin';
  /** True si el usuario es admin del sitio (profiles.role === 'admin'). */
  isSiteAdmin: boolean;
  /** Alias legacy de isSiteAdmin (foro, hoja de ruta). */
  isAdmin: boolean;
  /** Rol granular real en la empresa activa (null si no hay membership). */
  userRole: Role | null;
  /** Empresa activa seleccionada. */
  activeEmpresaId: string | null;
  /** Empresas a las que pertenece el usuario (para multi-empresa). */
  userCompanies: UserCompany[];
  /** Matriz de permisos en la empresa activa. */
  permissions: UserPermissions;
  /** True mientras cargan rol/permisos/empresas. */
  permissionsLoading: boolean;
  /**
   * True si el server confirmó la lista de memberships del usuario (online).
   * Sirve para distinguir "sin empresa confirmada" de "no se pudo validar
   * (offline)" — solo redirigimos a /sin-empresa cuando membershipConfirmed.
   */
  membershipConfirmed: boolean;
  can: (module: Module, action: Action) => boolean;
  hasRole: (roles: Role[]) => boolean;
  switchCompany: (empresaId: string) => Promise<void>;
  refreshPermissions: () => Promise<void>;
  authExpired: boolean;  // True when refresh token is invalid but app can still work offline
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;  // Aligned with mobile
  signUp: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;  // Aligned with mobile
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  clearAuthExpired: () => void;  // Clear the auth expired flag (after showing login modal)
}

// Exportado (crudo) para que otros providers anidados —p.ej. SessionProvider,
// que observa activeEmpresaId para aislar la sesión de carga por empresa—
// puedan leerlo con useContext sin usar el hook useAuth().
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<'usuario' | 'admin'>('usuario');
  const [userRole, setUserRole] = useState<Role | null>(null);
  const [activeEmpresaId, setActiveEmpresaId] = useState<string | null>(null);
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [permissions, setPermissions] = useState<UserPermissions>({});
  // Arranca en true: hasta que loadMembershipAndPermissions resuelva, el
  // PermissionGuard debe esperar (return null) en vez de redirigir a
  // /acceso-denegado con permisos vacíos. Esto evita una race condition al
  // cargar la app: getSession pone loading=false antes de que los permisos
  // del usuario se hayan cargado.
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [membershipConfirmed, setMembershipConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authExpired, setAuthExpired] = useState<boolean>(false);  // Aligned with mobile
  const lastProfileLoadUserId = useRef<string | null>(null);
  const lastProfileLoadTime = useRef<number>(0);
  // Último usuario al que se le ejecutó el purge de seguridad, para no repetirlo
  // en la misma sesión si loadMembershipAndPermissions se llama varias veces.
  const lastPurgedUserId = useRef<string | null>(null);
  // Empresas autorizadas conocidas (última carga exitosa). Sirve para el purge
  // robusto: si el server confirma menos empresas (o ninguna), purga las perdidas.
  const knownEmpresaIdsRef = useRef<string[]>([]);
  // Ref con la última versión de refreshPermissions (la suscripción al bus de
  // membership se arma una vez por usuario y necesita la versión actual).
  const refreshPermissionsRef = useRef<() => void>(() => {});
  // Último user_id cuya carga de permisos fue BLOQUEQUEANTE (permissionsLoading
  // = true). Las cargas siguientes para el mismo usuario son revalidaciones en
  // background y NO deben volver a poner permissionsLoading=true:
  // PermissionGuard devuelve null mientras loading, lo que desmontaría la
  // página completa y perdería el estado de los formularios (ej: al volver a
  // una pestaña en background, que dispara refresh de token + revalidación).
  // Una carga nueva de OTRO usuario (login) vuelve a ser bloqueante.
  const blockingPermissionsLoadFor = useRef<string | null>(null);
  // Último user_id validado vía eventos de auth: evita re-fetchear
  // perfil/permisos ante SIGNED_IN repetidos del mismo usuario (auth-js
  // emite SIGNED_IN en cada vuelta a visible).
  const lastValidatedUserIdRef = useRef<string | null>(null);

  /**
   * Clear auth expired flag (call after showing login modal)
   * Aligned with mobile AuthContext
   */
  const clearAuthExpired = useCallback(() => {
    setAuthExpired(false);
  }, []);

  /**
   * Carga el membership (rol granular), permisos y empresas del usuario en la
   * empresa activa. Se llama después de resolver el profile (que trae
   * empresa_activa_id). Offiline-first: si falla, deja permisos vacíos y la UI
   * funciona en modo degradado (la RLS de Supabase es la barrera real).
   */
  const loadMembershipAndPermissions = useCallback(
    async (currentUser: User | null, empresaId: string | null) => {
      if (!currentUser || !empresaId) {
        setUserRole(null);
        setActiveEmpresaId(null);
        setUserCompanies([]);
        setPermissions({});
        setMembershipConfirmed(false);
        knownEmpresaIdsRef.current = [];
        blockingPermissionsLoadFor.current = null;
        setPermissionsLoading(false);
        return;
      }

      // Bloqueante solo la primera carga de este usuario: las revalidaciones
      // en background (refresh de token al volver a la pestaña, broadcast de
      // membership) actualizan estado al resolver, sin desmontar la UI.
      const isBlockingLoad = blockingPermissionsLoadFor.current !== currentUser.id;
      if (isBlockingLoad) {
        setPermissionsLoading(true);
      }
      let confirmedWithServer = false;
      // Rol/permisos confirmados por el server en esta pasada (para persistir
      // la membresía como fallback offline).
      let confirmedRole: Role | null = null;
      let confirmedPerms: UserPermissions = {};
      try {
        // Traemos companies POR SEPARADO para distinguir "offline/error" de
        // "vacío real" (server confirmó 0 empresas = suspendido de todas).
        // getUserCompanies lanza en error de red; si la metemos en un Promise.all
        // arrastraría al rol/permisos y no podríamos diferenciar.
        let companies: UserCompany[];
        try {
          companies = await permissionsService.getUserCompanies(currentUser.id);
        } catch (e) {
          // Sin red: NO reseteamos nada (offline-friendly). Dejamos al usuario
          // con su empresa activa y caché local. membershipConfirmed queda como
          // esté (false en carga inicial) => el PrivateRoute no manda a
          // /sin-empresa por estar offline.
          // Restauro rol/permisos de la última membresía confirmada con el
          // server (persistida): sin esto, un arranque en frío offline dejaba
          // la matriz vacía y PermissionGuard redirigía a /acceso-denegado.
          const lastKnown = permissionsService.getLastKnownMembership(currentUser.id, empresaId);
          if (lastKnown) {
            console.log('[AuthContext] Offline: restaurando permisos persistidos (savedAt:', lastKnown.savedAt + ')');
            setUserRole(lastKnown.role);
            setPermissions(lastKnown.permissions);
            setActiveEmpresaId(empresaId);
          } else {
            console.warn('[AuthContext] Offline sin membresía persistida (primera carga offline del dispositivo)');
          }
          return;
        }

        setMembershipConfirmed(true);
        confirmedWithServer = true;
        const authorizedIds = companies.map((c) => c.empresa_id);

        // Determinar la empresa efectiva: si la empresa activa guardada ya no
        // está en las memberships activas del usuario (revocada), caer a la
        // primera empresa activa disponible y persistirlo en Supabase.
        let effectiveEmpresaId = empresaId;
        const belongsToActive = companies.some(
          (c) => c.empresa_id === empresaId && c.status === 'active'
        );
        if (!belongsToActive && companies.length > 0) {
          effectiveEmpresaId = companies[0].empresa_id;
          console.warn(
            `[AuthContext] Empresa activa ${empresaId} no pertenece al usuario; reseteando a ${effectiveEmpresaId}.`
          );
          // AWAIT + write local cache: si dejamos el UPDATE fire-and-forget,
          // SyncContext lee el ProfileRepository local con la empresa vieja
          // (suspendida) y suscribe el canal realtime de esa empresa, que el
          // server rechaza con 403 CHANNEL_ERROR. Hay que persistir el cambio
          // en el caché local antes de soltar el control.
          const { error: resetError } = await supabase
            .from('profiles')
            .update({ empresa_activa_id: effectiveEmpresaId, updated_at: new Date().toISOString() })
            .eq('user_id', currentUser.id);
          if (resetError) {
            console.warn('[AuthContext] reset empresa activa falló:', resetError.message);
          } else {
            try {
              const cached = await authService.getCurrentProfile();
              await authService.saveProfile({
                user_id: currentUser.id,
                empresa_activa_id: effectiveEmpresaId,
                role: cached?.role ?? 'usuario',
              });
            } catch (err) {
              console.warn('[AuthContext] saveProfile local tras reset falló:', err);
            }
          }
          const [rolEff, permsEff] = await Promise.all([
            permissionsService.getUserRole(currentUser.id, effectiveEmpresaId),
            permissionsService.getUserPermissions(currentUser.id, effectiveEmpresaId),
          ]);
          setUserRole(rolEff);
          setPermissions(permsEff);
          confirmedRole = rolEff;
          confirmedPerms = permsEff;
        } else if (companies.length === 0) {
          // Server confirmó 0 empresas activas (suspendido/removido de todas).
          // Sin rol/permisos. Mantenemos activeEmpresaId apuntando a la empresa
          // muerta solo para contexto; el PrivateRoute redirige a /sin-empresa.
          setUserRole(null);
          setPermissions({});
        } else {
          const [rol, perms] = await Promise.all([
            permissionsService.getUserRole(currentUser.id, effectiveEmpresaId),
            permissionsService.getUserPermissions(currentUser.id, effectiveEmpresaId),
          ]);
          setUserRole(rol);
          setPermissions(perms);
          confirmedRole = rol;
          confirmedPerms = perms;
        }

        setActiveEmpresaId(companies.length > 0 ? effectiveEmpresaId : empresaId);
        setUserCompanies(companies);

        // Persistir la última membresía confirmada con el server (fallback
        // offline para el arranque en frío sin conexión), o limpiarla si el
        // server confirmó la suspensión total del usuario.
        if (companies.length > 0 && confirmedRole) {
          permissionsService.saveLastKnownMembership(
            currentUser.id,
            effectiveEmpresaId,
            confirmedRole,
            confirmedPerms,
          );
        } else if (companies.length === 0) {
          permissionsService.clearLastKnownMembership(currentUser.id, empresaId);
        }

        // Security: purge robusto de datos locales de empresas a las que el
        // usuario ya no pertenece (fix 3).
        //   - Si el server confirmó empresas, purga todo lo que NO esté en la
        //     lista (incluye empresas recientemente perdidas).
        //   - Si confirmó 0 empresas Y antes tenía alguna (suspensión total),
        //     purga TODA la caché empresa-scoped (allowPurgeAll).
        //   - Una vez por usuario (guard) para no repetir en la misma sesión.
        const hadKnown = knownEmpresaIdsRef.current.length > 0;
        const isFirstLoadForUser = lastPurgedUserId.current !== currentUser.id;
        if (companies.length > 0 && (hadKnown || isFirstLoadForUser)) {
          lastPurgedUserId.current = currentUser.id;
          purgeUnauthorizedLocalData(authorizedIds).catch((err) =>
            console.warn('[AuthContext] purge failed:', err)
          );
        } else if (companies.length === 0 && hadKnown) {
          lastPurgedUserId.current = currentUser.id;
          purgeUnauthorizedLocalData([], { allowPurgeAll: true }).catch((err) =>
            console.warn('[AuthContext] purge-all failed:', err)
          );
        }
        knownEmpresaIdsRef.current = authorizedIds;
      } catch (error) {
        // Error TRANSITORIO (timeout, red inestable, 500): NO reseteamos
        // permisos. Mismo principio offline-friendly que el early-return de
        // getUserCompanies: vaciarlos aquí blanqueaba el menú (los items se
        // filtran por can()) y dejaba al usuario sin navegar hasta la
        // próxima revalidación exitosa. Solo se limpian con confirmación del
        // server (companies.length === 0 = suspensión real).
        console.error('[AuthContext] Error loading membership/permissions (se mantienen los permisos actuales):', error);
      } finally {
        // Recién acá la carga bloqueante se da por hecha: si falló (offline),
        // el próximo intento para este usuario vuelve a ser bloqueante.
        if (confirmedWithServer) {
          blockingPermissionsLoadFor.current = currentUser.id;
        }
        setPermissionsLoading(false);
      }
    },
    [],
  );

  // Load profile using shared authService
  const loadProfileFromSupabase = useCallback(async (currentUser: User | null, forceRefresh = false) => {
    if (!currentUser) {
      console.log('AUTH: No current user, setting role to usuario');
      setRole('usuario');
      await loadMembershipAndPermissions(null, null);
      return;
    }

    // Prevent duplicate calls for the same user within a short time window
    if (!forceRefresh &&
        lastProfileLoadUserId.current === currentUser.id) {
      console.log('AUTH: Skipping duplicate profile load for user:', currentUser.id);
      return;
    }

    lastProfileLoadUserId.current = currentUser.id;

    // Use shared authService (handles cache, fetch, and save)
    const profile = await authService.loadUserProfile(currentUser.id, forceRefresh);

    if (profile?.role) {
      setRole(profile.role as 'usuario' | 'admin');
    } else {
      setRole('usuario');  // Default fallback
    }

    // Cargar rol granular + permisos + empresas para la empresa activa.
    await loadMembershipAndPermissions(currentUser, profile?.empresa_activa_id ?? null);
  }, [loadMembershipAndPermissions]);

  useEffect(() => {
    let mounted = true;

    // Obtenemos la sesión actual al cargar la aplicación
    // OFFLINE-FIRST: Set a timeout to show UI even if network is slow
    // Aligned with mobile AuthContext
    const getSession = async () => {
      // Set a short timeout (3 seconds) to show UI even if network is slow
      // This ensures the app is usable offline
      const timeoutId = setTimeout(() => {
        if (mounted && loading) {
          console.log('[AuthContext] Session check taking too long, showing UI anyway');
          setLoading(false);
        }
      }, 3000);

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!mounted) return;

        clearTimeout(timeoutId);

        if (error && isInvalidRefreshTokenError(error)) {
          console.log('[AuthContext] Invalid refresh token detected, auth expired');
          setAuthExpired(true);
          setUser(null);
          setLoading(false);
          return;
        }

        const currentUser = session?.user ?? null;
        setUser(currentUser);

        // Clear auth expired if we have a valid session
        if (currentUser) {
          setAuthExpired(false);
        }

        // Crucial: Set loading to false as soon as we have the user session
        // Profile loading is handled by onAuthStateChange which fires immediately
        setLoading(false);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('AUTH: Error in getSession', error);
        if (mounted) setLoading(false);
      }
    };

    getSession();

    // Escuchamos cambios en el estado de autenticación (LOGIN, LOGOUT, etc.)
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session: Session | null) => {
        if (!mounted) return;

        const currentUser = session?.user ?? null;
        // Preservar la identidad del objeto `user` cuando es el mismo usuario:
        // cada evento de auth trae un objeto nuevo y el cambio de identidad
        // re-renderiza todos los consumidores del contexto y re-suscribe el
        // bus de membership (el "membership bus CLOSED → SUBSCRIBED" en cada
        // refresh de token).
        setUser((prev) => (prev?.id === currentUser?.id ? prev : currentUser));

        // Handle TOKEN_REFRESHED event - clear auth expired if successful
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Token refreshed successfully');
          setAuthExpired(false);
        }

        // Handle signed out event
        if (event === 'SIGNED_OUT') {
          setAuthExpired(false);
          // Limpiar la membresía persistida (rol/permisos fallback offline):
          // no debe sobrevivir a un logout en el dispositivo.
          permissionsService.clearLastKnownMembership();
        }

        // If we get a session change, we should also update the profile
        if (currentUser) {
          // Revalidamos perfil+permisos SOLO en TOKEN_REFRESHED (el token
          // rotó de verdad: ~cada 1h, o al volver a una pestaña background
          // con el token ya expirado) o ante un usuario distinto de la última
          // validación (primera carga / cambio de usuario). auth-js emite
          // SIGNED_IN en CADA vuelta a visible aunque no cambie nada —
          // ignorarlo evita el fetch de perfil+permisos+purge por cambio de
          // pestaña; los cambios de permisos en vivo los cubre el bus de
          // membership (`user:{id}`, inmediato). Este fetch de ~1h queda como
          // fallback ante broadcasts perdidos (peor caso de detección de una
          // revocación: ~1h; la RLS de Supabase es la barrera real).
          if (
            event === 'TOKEN_REFRESHED' ||
            currentUser.id !== lastValidatedUserIdRef.current
          ) {
            lastValidatedUserIdRef.current = currentUser.id;
            // Load profile to get empresa_activa_id - don't await to avoid blocking UI
            loadProfileFromSupabase(currentUser, true); // Force refresh on auth state change
          }
        } else {
          setRole('usuario');
        }

        setLoading(false);
      }
    );

    // Limpiamos el listener cuando el componente se desmonta
    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [loadProfileFromSupabase]);

  /**
   * Recarga permisos/rol. Vuelve a leer el profile desde el server (force)
   * antes que los permisos, para capturar cambios server-side en
   * `empresa_activa_id` y memberships.
   *
   * Importante: NO depender del `activeEmpresaId` del estado de React aquí.
   * Tras aceptar una invitación, la RPC `accept_invitation` setea
   * `empresa_activa_id` server-side, pero el estado local sigue en `null`
   * (el usuario no tenía empresa antes). Si filtráramos por `activeEmpresaId`,
   * esta función sería no-op y el dashboard quedaría sin secciones (rol/permisos
   * vacíos) hasta el próximo token refresh. `loadProfileFromSupabase(force)`
   * releee el `empresa_activa_id` correcto y dispara `loadMembershipAndPermissions`.
   *
   * useCallback + ref: la suscripción al bus de membership (más abajo) se arma
   * una vez por usuario y necesita la versión más fresca de esta función.
   */
  const refreshPermissions = useCallback(async () => {
    if (!user) return;
    permissionsService.clearCache();
    await loadProfileFromSupabase(user, true);
  }, [user, loadProfileFromSupabase]);

  useEffect(() => {
    refreshPermissionsRef.current = refreshPermissions;
  }, [refreshPermissions]);

  /**
   * Suscripción al bus de membership `user:{id}` (fix 1).
   * Ante cualquier cambio en `company_users` del usuario (suspenden, reactivan,
   * cambian rol, lo agregan a otra empresa, lo eliminan), recargamos permisos.
   * Es un bus (canal único por usuario); se rutea por `payload.table`. Hoy solo
   * nos interesa `company_users`, pero el patrón es extensible a otras tablas
   * user-scoped. Debounce para coalescear bursts.
   *
   * DEDUP por signature: si el mismo (table + op + id) llega varias veces
   * dentro de una ventana de 2s (típico de redelivery de Realtime o de
   * triggers que disparan varias veces para el mismo UPDATE), se ignora.
   * Eso evita los 3 ciclos de refreshPermissions que se veían por cada
   * suspensión.
   */
  useEffect(() => {
    if (!user) return;
    let channel: RealtimeChannel | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let lastSig: string | null = null;
    let lastSigAt = 0;
    const DEDUP_WINDOW_MS = 2000;

    const isMembership = (msg: any) => msg?.payload?.table === 'company_users';
    const onMembershipChange = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        refreshPermissionsRef.current?.();
      }, 500);
    };
    const onMsg = (msg: any) => {
      // Log crudo para diagnóstico: confirma si el broadcast llega y de qué tabla.
      console.log('[AuthContext] membership bus msg:', msg?.event, msg?.payload);
      if (!isMembership(msg)) return;

      // Dedup por signature dentro de la ventana. El payload trae
      // { table, op, id, user_id }; construimos un key estable.
      const p = msg?.payload ?? {};
      const sig = `${p.table}|${p.op}|${p.id ?? ''}|${p.user_id ?? ''}`;
      const now = Date.now();
      if (sig === lastSig && (now - lastSigAt) < DEDUP_WINDOW_MS) {
        console.log('[AuthContext] membership bus msg duplicado, ignorando');
        return;
      }
      lastSig = sig;
      lastSigAt = now;
      onMembershipChange();
    };

    (async () => {
      // En este proyecto el server solo entrega broadcasts a canales `private`
      // (autenticados con JWT). Animales usa private y entrega; un canal público
      // se suscribe pero NO recibe el broadcast. Por eso private.
      // Authenticamos la conexión realtime con el token EXPLÍCITO antes de
      // suscribir (este effect se monta muy temprano, apenas se setea `user`;
      // si no pasamos el token a mano, hay una carrera y el handshake private
      // larga TIMED_OUT/CLOSED).
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await supabase.realtime.setAuth(session.access_token);
        }
      } catch {
        /* ignore */
      }
      channel = supabase
        .channel(`user:${user.id}`, { config: { private: true } })
        .on('broadcast', { event: 'INSERT' }, onMsg)
        .on('broadcast', { event: 'UPDATE' }, onMsg)
        .on('broadcast', { event: 'DELETE' }, onMsg)
        .subscribe((status, err) => {
          console.log('[AuthContext] membership bus state:', status, err ?? '');
        });
    })();

    return () => {
      if (debounce) clearTimeout(debounce);
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);

  const value = {
    user,
    role,
    isSiteAdmin: role === 'admin',
    isAdmin: role === 'admin', // alias legacy (site-admin): foro, hoja de ruta
    userRole,
    activeEmpresaId,
    userCompanies,
    permissions,
    permissionsLoading,
    membershipConfirmed,
    can: (module: Module, action: Action) => permissionsService.can(permissions, module, action),
    hasRole: (roles: Role[]) => !!userRole && roles.includes(userRole),

    /**
     * Cambia la empresa activa del usuario. Actualiza profiles.empresa_activa_id
     * en Supabase (validado por RLS), el profile local, y recarga permisos.
     */
    switchCompany: async (empresaId: string) => {
      if (!user) return;
      // 1. Revalidar membership activa SERVER-SIDE (fix 4). El `userCompanies`
      // local puede estar stale (suspendido hace un instante y el refresh aún
      // no terminó). getUserRole filtra status='active' => null si ya no hay
      // acceso. La RLS de profiles también lo frenaría, pero así damos un
      // mensaje claro. (Lanza en error de red: un fallo transitorio NO se
      // interpreta como "sin acceso" — se reintenta con refreshPermissions.)
      let currentRole: Role | null = null;
      try {
        currentRole = await permissionsService.getUserRole(user.id, empresaId);
      } catch (e) {
        console.warn('[AuthContext] No se pudo validar membership en switch (transitorio):', e);
        await refreshPermissions();
        throw new Error('No se pudo verificar el acceso a esa empresa. Revisá tu conexión e intentá de nuevo.');
      }
      if (!currentRole) {
        console.warn('[AuthContext] Sin membership activa (server) en la empresa:', empresaId);
        await refreshPermissions();
        throw new Error('Ya no tenés acceso a esa empresa (te suspendieron o removieron).');
      }

      // 2. Evitar cambios pendientes huérfanos: si hay cambios sin sincronizar de
      // la empresa actual, empujarlos ANTES de cambiar empresa_activa_id (la RLS
      // los rechazaría después porque empresa_id ya no coincidiría con la empresa
      // activa). Si estamos offline con pendientes, abortar el switch para no
      // perderlos; si no hay pendientes, se puede cambiar sin problema.
      try {
        const pendingCount = await sync.getPendingCount?.();
        if (pendingCount && pendingCount > 0) {
          if (!sync.isOnline?.()) {
            throw new Error(
              `Tenés ${pendingCount} cambio(s) sin sincronizar de la empresa actual. ` +
              `Volvé a estar online para poder cambiar de empresa sin perderlos.`,
            );
          }
          await sync.syncToRemote?.();
        }
      } catch (e) {
        console.error('[AuthContext] No se pudo flushear pendientes antes del switch:', e);
        throw e;
      }

      // 3. Actualizar empresa activa en Supabase.
      const { error } = await supabase
        .from('profiles')
        .update({ empresa_activa_id: empresaId, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (error) {
        console.error('[AuthContext] Error switching company:', error.message);
        throw error;
      }

      // 4. Actualizar profile local PRIMERO, antes de propagar activeEmpresaId.
      // El profile local es la fuente de verdad de getCurrentProfile() en los
      // repositorios (caché TTL 5s que se invalida con el evento 'profiles' que
      // emite saveProfile). Si propagamos activeEmpresaId antes de persistirlo,
      // el re-render de AnimalesPage filtra con la empresa VIEJA (caché stale); y
      // si después el catch-up de realtime no escribe animales (no hay cambios),
      // no existe un segundo re-render → la tabla queda mostrando la empresa
      // equivocada. Persistir primero garantiza que el re-render disparado por
      // activeEmpresaId ya lea la empresa correcta.
      try {
        const profile = await authService.loadUserProfile(user.id, true);
        await authService.saveProfile({
          user_id: user.id,
          empresa_activa_id: empresaId,
          role: (profile?.role ?? 'usuario') as 'usuario' | 'admin',
        });
      } catch (err) {
        console.warn('[AuthContext] No se pudo refrescar profile local tras switch:', err);
      }

      // 5. Invalidar cache de permisos y recargar (propaga activeEmpresaId).
      permissionsService.clearCache();
      await loadMembershipAndPermissions(user, empresaId);

      // 6. Forzar recarga completa de los datos de la nueva empresa (init() es
      // no-op tras el primer login y el sync incremental no traería registros
      // viejos).
      sync.reloadAfterCompanySwitch?.(empresaId).catch((err: Error) =>
        console.warn('[AuthContext] Reload after company switch failed:', err),
      );
    },

    /**
     * Recarga permisos/rol (definido como useCallback más arriba). Ver doc ahí.
     */
    refreshPermissions,

    authExpired,
    // Sign in with email and password (aligned with mobile AuthContext)
    signIn: async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // Load user profile with empresa_activa_id BEFORE triggering sync
      // This ensures empresa_id is available for repository operations
      if (data.session?.user) {
        marcarHaIngresado(); // KAN-289: el CTA público pasa a "Ingresar" en este dispositivo
        console.log('[AuthContext] Login successful, loading profile...');
        await loadProfileFromSupabase(data.session.user, true); // Force refresh from server

        // Trigger sync immediately after profile is loaded (aligned with mobile)
        console.log('[AuthContext] Profile loaded, triggering sync...');
        sync.init().catch((err: Error) => {
          console.warn('[AuthContext] Sync after login failed:', err);
        });
      }

      return data;
    },
    // Sign up new user (aligned with mobile AuthContext)
    signUp: async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // Load user profile with empresa_activa_id BEFORE triggering sync
      // This ensures empresa_id is available for repository operations
      if (data.session?.user) {
        console.log('[AuthContext] Signup successful, loading profile...');
        await loadProfileFromSupabase(data.session.user, true); // Force refresh from server

        // Trigger sync immediately after profile is loaded (aligned with mobile)
        console.log('[AuthContext] Profile loaded, triggering sync...');
        sync.init().catch((err: Error) => {
          console.warn('[AuthContext] Sync after signup failed:', err);
        });
      }

      return data;
    },
    signOut: async () => {
      marcarHaIngresado(); // KAN-289: cerrar sesión implica que hay cuenta en este dispositivo
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error al cerrar sesión:', error);
      }
      // Resetear el estado multi-empresa (activeEmpresaId -> null). Esto también
      // dispara en SessionProvider la limpieza de la sesión de carga activa,
      // para que otro usuario en el mismo navegador no la herede.
      await loadMembershipAndPermissions(null, null);
    },
    resetPassword: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    },
    updatePassword: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { error };
    },
    clearAuthExpired,
  };

  // No renderizamos la app hasta que sepamos si hay un usuario o no
  // Pero ahora 'loading' solo depende de tener la sesión, no el rol.
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto
/** Como useAuth, pero devuelve undefined fuera de un AuthProvider (componentes compartidos). */
export const useAuthOptional = (): AuthContextType | undefined => useContext(AuthContext);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
